import { PhoneIncoming, PhoneOutgoing, Sparkles } from 'lucide-react'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'

interface Props {
  isOpen: boolean
  onClose: () => void
  onSelectType: (type: 'inbound' | 'outbound') => void
}

/** Port 1:1 du Next AgentTypeSelectionModal — choix inbound/outbound au lancement. */
export function AgentTypeSelectionModal({ isOpen, onClose, onSelectType }: Props) {
  const { t } = useTranslation()
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0 shadow-2xl shadow-black/20 dark:shadow-black/50">
        <div className="h-1 w-full bg-gradient-to-r from-callizy-blue via-blue-400 to-callizy-blue" />

        <div className="p-6 pb-4">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-callizy-blue/10 to-callizy-blue/5 ring-1 ring-callizy-blue/20">
              <Sparkles className="h-5 w-5 text-callizy-blue" />
            </div>
            <div>
              <DialogTitle className="font-display text-xl tracking-tight">
                {t('manager.aiAgents.createAgent')}
              </DialogTitle>
              <VisuallyHidden>
                <DialogDescription>
                  {t('manager.aiAgents.chooseAgentType')}
                </DialogDescription>
              </VisuallyHidden>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('manager.aiAgents.chooseAgentType')}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 pt-2 space-y-3">
          {/* Inbound */}
          <button
            type="button"
            onClick={() => onSelectType('inbound')}
            className="group w-full relative p-5 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 bg-white dark:bg-slate-800/50 hover:bg-blue-50/50 dark:hover:bg-blue-950/30 transition-all duration-200 text-left shadow-sm hover:shadow-md"
          >
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20 group-hover:shadow-blue-500/40 group-hover:scale-105 transition-all duration-200">
                <PhoneIncoming className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                    {t('manager.aiAgents.inboundAgent')}
                  </h3>
                  <span className="px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-full">
                    {t('manager.aiAgents.reception')}
                  </span>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {t('manager.aiAgents.inboundAgentDescription')}
                </p>
              </div>
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center group-hover:bg-blue-100 dark:group-hover:bg-blue-900/50 group-hover:translate-x-1 transition-all duration-200">
                <svg className="w-4 h-4 text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </button>

          {/* Outbound */}
          <button
            type="button"
            onClick={() => onSelectType('outbound')}
            className="group w-full relative p-5 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-emerald-300 dark:hover:border-emerald-600 bg-white dark:bg-slate-800/50 hover:bg-emerald-50/50 dark:hover:bg-emerald-950/30 transition-all duration-200 text-left shadow-sm hover:shadow-md"
          >
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/20 group-hover:shadow-emerald-500/40 group-hover:scale-105 transition-all duration-200">
                <PhoneOutgoing className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                    {t('manager.aiAgents.outboundAgent')}
                  </h3>
                  <span className="px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 rounded-full">
                    {t('manager.aiAgents.campaign')}
                  </span>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {t('manager.aiAgents.outboundAgentDescription')}
                </p>
              </div>
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/50 group-hover:translate-x-1 transition-all duration-200">
                <svg className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
